import React, { useEffect, useState } from 'react';

const SpartaHeatmap = ({ spartaEntities = {}, mitreTechniques = [] }) => {
    const [heatmapData, setHeatmapData] = useState([]);
    const [loading, setLoading] = useState(true);

    // SPARTA Framework Structure (simplified like MITRE)
    const spartaMatrix = {
        'SA001': { name: 'Satellite Hijacking', segment: 'Space Segment' },
        'SA002': { name: 'Signal Spoofing', segment: 'Space Segment' },
        'SA003': { name: 'Command Injection', segment: 'Space Segment' },
        'SA004': { name: 'Orbit Manipulation', segment: 'Space Segment' },
        'SA005': { name: 'Ground Station Infiltration', segment: 'Ground Segment' },
        'SA006': { name: 'Control System Compromise', segment: 'Ground Segment' },
        'SA007': { name: 'Data Manipulation', segment: 'Ground Segment' },
        'SA008': { name: 'Mission Control Takeover', segment: 'Ground Segment' },
        'SA009': { name: 'Signal Jamming', segment: 'Link Segment' },
        'SA010': { name: 'Frequency Hopping Attack', segment: 'Link Segment' },
        'SA011': { name: 'Link Encryption Break', segment: 'Link Segment' },
        'SA012': { name: 'Beam Steering Attack', segment: 'Link Segment' },
        'SA013': { name: 'Terminal Compromise', segment: 'User Segment' },
        'SA014': { name: 'User Equipment Spoofing', segment: 'User Segment' },
        'SA015': { name: 'Service Denial', segment: 'User Segment' },
        'SA016': { name: 'Data Interception', segment: 'User Segment' },
        'SA017': { name: 'Supply Chain Attack', segment: 'Support Infrastructure' },
        'SA018': { name: 'Development Environment Compromise', segment: 'Support Infrastructure' },
        'SA019': { name: 'Testing Infrastructure Attack', segment: 'Support Infrastructure' },
        'SA020': { name: 'Launch System Interference', segment: 'Support Infrastructure' }
    };

    // MITRE ATT&CK to SPARTA Overlap Mapping
    const frameworkOverlap = {
        'Command and Control': {
            mitre: ['T1071', 'T1573', 'T1090', 'T1105'],
            sparta: ['SA009', 'SA010', 'SA011'],
            overlap: 'Communication channel control',
            riskMultiplier: 1.5
        },
        'Initial Access': {
            mitre: ['T1566', 'T1190', 'T1133'],
            sparta: ['SA005', 'SA006', 'SA013'],
            overlap: 'System entry points',
            riskMultiplier: 1.3
        },
        'Discovery': {
            mitre: ['T1083', 'T1057', 'T1018', 'T1082'],
            sparta: ['SA007', 'SA014', 'SA019'],
            overlap: 'System reconnaissance',
            riskMultiplier: 1.2
        },
        'Collection': {
            mitre: ['T1005', 'T1039', 'T1025'],
            sparta: ['SA012', 'SA015', 'SA020'],
            overlap: 'Data gathering',
            riskMultiplier: 1.4
        },
        'Exfiltration': {
            mitre: ['T1041', 'T1048', 'T1052'],
            sparta: ['SA011', 'SA016', 'SA018'],
            overlap: 'Data extraction',
            riskMultiplier: 1.6
        }
    };

    // Helper function to check for SPARTA-MITRE overlaps
    const checkFrameworkOverlap = (spartaId, mitreList) => {
        for (const category of Object.values(frameworkOverlap)) {
            if (category.sparta.includes(spartaId)) {
                const hasMatchingMitre = category.mitre.some(mitreId => 
                    mitreList.some(technique => {
                        if (typeof technique === 'string') {
                            return technique.includes(mitreId) || technique.startsWith(mitreId);
                        }
                        return false;
                    })
                );
                if (hasMatchingMitre) {
                    return {
                        hasOverlap: true,
                        category: category.overlap,
                        riskMultiplier: category.riskMultiplier
                    };
                }
            }
        }
        return { hasOverlap: false, category: null, riskMultiplier: 1.0 };
    };

    useEffect(() => {
        const processHeatmapData = () => {
            const data = [];
            
            // Extract SPARTA techniques from entities
            const extractedTechniques = [];
            if (spartaEntities.sparta_techniques) {
                extractedTechniques.push(...spartaEntities.sparta_techniques);
            }
            if (spartaEntities.space_threats) {
                extractedTechniques.push(...spartaEntities.space_threats);
            }
            if (spartaEntities.space_assets) {
                extractedTechniques.push(...spartaEntities.space_assets);
            }
            
            // Extract technique IDs from objects and strings
            const techniqueIds = extractedTechniques.map(technique => {
                if (typeof technique === 'string') {
                    const match = technique.match(/SA\d{3}/);
                    return match ? match[0] : null;
                } else if (technique && typeof technique === 'object') {
                    return technique.id || technique.name || null;
                }
                return null;
            }).filter(Boolean);

            console.log('Extracted SPARTA technique IDs:', techniqueIds);
            console.log('MITRE techniques for overlap:', mitreTechniques);

            // Create heatmap data for each SPARTA technique
            Object.entries(spartaMatrix).forEach(([techId, techInfo]) => {
                const isDetected = techniqueIds.includes(techId) || 
                    extractedTechniques.some(t => {
                        if (typeof t === 'string') {
                            return t.includes(techId) || t.toLowerCase().includes(techInfo.name.toLowerCase());
                        } else if (t && typeof t === 'object' && t.id) {
                            return t.id === techId;
                        }
                        return false;
                    });
                
                // Check for SPARTA-MITRE overlap
                const overlapInfo = checkFrameworkOverlap(techId, mitreTechniques);
                
                let intensity = 0;
                let cellType = 'none';
                
                if (isDetected && overlapInfo.hasOverlap) {
                    intensity = 0.9; // Critical overlap - both SPARTA and MITRE detected
                    cellType = 'critical-overlap';
                } else if (isDetected) {
                    intensity = 0.7; // SPARTA only
                    cellType = 'sparta-only';
                } else if (overlapInfo.hasOverlap) {
                    intensity = 0.4; // MITRE overlap potential
                    cellType = 'mitre-potential';
                }
                
                console.log(`Technique ${techId}: detected=${isDetected}, overlap=${overlapInfo.hasOverlap}, type=${cellType}`);

                data.push({
                    technique: techId,
                    name: techInfo.name,
                    segment: techInfo.segment,
                    intensity: intensity,
                    detected: isDetected,
                    hasOverlap: overlapInfo.hasOverlap,
                    overlapCategory: overlapInfo.category,
                    riskMultiplier: overlapInfo.riskMultiplier,
                    cellType: cellType
                });
            });
            
            setHeatmapData(data);
            setLoading(false);
        };

        processHeatmapData();
    }, [spartaEntities, mitreTechniques]);

    const renderHeatmap = () => {
        if (heatmapData.length === 0) return null;

        const segments = [...new Set(heatmapData.map(d => d.segment))];
        const techniques = [...new Set(heatmapData.map(d => d.technique))].sort();

        const cellSize = 40;
        const margin = { top: 100, right: 50, bottom: 50, left: 150 };
        const width = techniques.length * cellSize + margin.left + margin.right;
        const height = segments.length * cellSize + margin.top + margin.bottom;

        const colorScale = (d) => {
            if (d.intensity === 0) return '#f8f9fa'; // No detection
            
            switch (d.cellType) {
                case 'critical-overlap':
                    return '#495057'; // Dark gray - Critical overlap (both SPARTA and MITRE)
                case 'sparta-only':
                    return '#6f42c1'; // Purple - SPARTA technique detected
                case 'mitre-potential':
                    return '#fd7e14'; // Orange - MITRE overlap potential
                default:
                    return '#17a2b8'; // Blue - General space threat
            }
        };

        return (
            <svg width={width} height={height} className="sparta-heatmap-svg">
                {/* Segment labels */}
                {segments.map((segment, i) => (
                    <text
                        key={segment}
                        x={margin.left - 10}
                        y={margin.top + i * cellSize + cellSize / 2}
                        textAnchor="end"
                        dominantBaseline="middle"
                        fontSize="12"
                        fill="#333"
                    >
                        {segment}
                    </text>
                ))}

                {/* Technique labels */}
                {techniques.map((technique, i) => (
                    <text
                        key={technique}
                        x={margin.left + i * cellSize + cellSize / 2}
                        y={margin.top - 10}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize="10"
                        fill="#333"
                        transform={`rotate(-45, ${margin.left + i * cellSize + cellSize / 2}, ${margin.top - 10})`}
                    >
                        {technique}
                    </text>
                ))}

                {/* Heatmap cells */}
                {heatmapData.map((d, i) => {
                    const segmentIndex = segments.indexOf(d.segment);
                    const techniqueIndex = techniques.indexOf(d.technique);
                    
                    return (
                        <rect
                            key={i}
                            x={margin.left + techniqueIndex * cellSize}
                            y={margin.top + segmentIndex * cellSize}
                            width={cellSize - 1}
                            height={cellSize - 1}
                            fill={colorScale(d)}
                            stroke="#fff"
                            strokeWidth="1"
                            className="sparta-cell"
                            title={`${d.name} (${d.technique}): ${d.detected ? 'DETECTED' : 'Not Detected'}${d.hasOverlap ? ` | MITRE Overlap: ${d.overlapCategory}` : ''}`}
                        />
                    );
                })}
            </svg>
        );
    };

    if (loading) {
        return (
            <div className="card">
                <div className="card-body text-center py-5">
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Generating heatmap...</span>
                    </div>
                    <p className="text-muted">Processing SPARTA data...</p>
                </div>
            </div>
        );
    }

    const activeCells = heatmapData.filter(d => d.intensity > 0).length;
    const totalCells = heatmapData.length;
    const overlapCells = heatmapData.filter(d => d.hasOverlap).length;
    const criticalOverlaps = heatmapData.filter(d => d.cellType === 'critical-overlap').length;

    return (
        <div className="card">
            <div className="card-header">
                <h6 className="mb-0">
                    <i className="fas fa-satellite me-2"></i>
                    SPARTA Framework Heatmap
                    <span className="badge bg-danger ms-2">
                        {activeCells}/{totalCells} techniques
                    </span>
                </h6>
            </div>
            <div className="card-body">
                {activeCells === 0 ? (
                    <div className="text-center py-5">
                        <i className="fas fa-satellite fa-3x text-muted mb-3"></i>
                        <h5 className="text-muted">No SPARTA Data</h5>
                        <p className="text-muted">
                            No SPARTA techniques were identified in this document.
                        </p>
                    </div>
                ) : (
                    <div>
                        <div className="mb-3">
                            <small className="text-muted">
                                This heatmap shows SPARTA space cybersecurity techniques and their overlaps with MITRE ATT&CK.
                                Critical overlaps indicate techniques present in both frameworks.
                            </small>
                        </div>
                        
                        <div className="overflow-auto">
                            {renderHeatmap()}
                        </div>
                        
                        {/* Legend */}
                        <div className="sparta-legend mt-3" style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ width: '20px', height: '20px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '3px' }}></div>
                                <span style={{ fontSize: '0.9em' }}>No Threat</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ width: '20px', height: '20px', backgroundColor: '#17a2b8', borderRadius: '3px' }}></div>
                                <span style={{ fontSize: '0.9em' }}>Space Threat</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ width: '20px', height: '20px', backgroundColor: '#6f42c1', borderRadius: '3px' }}></div>
                                <span style={{ fontSize: '0.9em' }}>SPARTA Only</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ width: '20px', height: '20px', backgroundColor: '#fd7e14', borderRadius: '3px' }}></div>
                                <span style={{ fontSize: '0.9em' }}>MITRE Overlap</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ width: '20px', height: '20px', backgroundColor: '#495057', borderRadius: '3px' }}></div>
                                <span style={{ fontSize: '0.9em' }}>Critical Overlap</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            <div className="card-footer">
                <div className="row">
                    <div className="col-md-4">
                        <small className="text-muted">
                            <i className="fas fa-satellite me-1"></i>
                            SPARTA: {activeCells}/{Object.keys(spartaMatrix).length}
                        </small>
                    </div>
                    <div className="col-md-4 text-center">
                        <small className="text-muted">
                            <i className="fas fa-link me-1"></i>
                            Overlaps: {overlapCells} ({criticalOverlaps} critical)
                        </small>
                    </div>
                    <div className="col-md-4 text-end">
                        <small className="text-muted">
                            Coverage: {totalCells > 0 ? ((activeCells / totalCells) * 100).toFixed(1) : 0}%
                        </small>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SpartaHeatmap;
